import { RiskBadge } from '@/components/RiskBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { WidgetTable } from '@/components/WidgetTable';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { buildGroupedAlertDeepLinkUrl } from '@/pages/organization/tabs/DataTableAlertsTab/utils';
import { useQuery } from '@tanstack/react-query';
import { listOrganizationGroupedAlerts } from '@kirin/api-client/api/alerts';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { OrganizationScanAlertsSortField } from '@kirin/api-client/contracts/alerts';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { apiClient } from '@/lib/api-client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  formatSubPolicyDescription,
  formatSubPolicyLabel,
} from '@kirin/api-client/helpers/alerts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';

const LIMIT = 10;

export function LatestAlertsWidget(): React.ReactNode {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.ALERTS_REVAMP]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;

  const handleOnClickAlert = (alert: GroupedAlert) => {
    const url = buildGroupedAlertDeepLinkUrl(organizationId, alert);
    if (url) {
      navigate(url);
    }
  };

  const { isLoading, data, isError, refetch } = useQuery({
    queryKey: ['alerts', 'organization', organizationId ?? '', 'latest'],
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listOrganizationGroupedAlerts(apiClient, organizationId, {
        page: 1,
        limit: LIMIT,
        sortBy: OrganizationScanAlertsSortField.LastSeenAt,
        sortOrder: SortOrder.Desc,
      });
    },
    enabled: !!organizationId,
  });

  const alerts: GroupedAlert[] =
    (data && 'data' in data ? data.data : data?.alerts) ?? [];

  return (
    <DashboardWidgetWrapper
      isLoading={isLoading}
      isError={isError}
      className="xl:col-span-5"
      title="Latest Alerts"
      description="Shows up to 10 Alerts"
      refetch={refetch}
    >
      <WidgetTable<GroupedAlert>
        data={alerts}
        className="flex-1 min-h-0"
        columns={[
          {
            id: 'type',
            header: revampEnabled ? 'Title' : 'Type',
            cell: a => {
              const label = revampEnabled
                ? formatSubPolicyLabel(a.subPolicyKey, a.type)
                : a.type;
              return (
                <span
                  className="font-medium truncate block max-w-[160px]"
                  title={label}
                >
                  {label}
                </span>
              );
            },
            className: 'max-w-[120px]',
          },
          {
            id: 'severity',
            header: 'Severity',
            cell: a => <RiskBadge risk={a.severity} showIcon />,
            className: 'min-w-[80px]',
          },
          {
            id: 'description',
            header: 'Description',
            cell: a => {
              const description = revampEnabled
                ? formatSubPolicyDescription(
                    a.subPolicyKey,
                    a.type,
                    a.description
                  )
                : a.description;
              return (
                <span
                  className="truncate block max-w-[200px]"
                  title={description}
                >
                  {description}
                </span>
              );
            },
          },
          {
            id: 'lastSeen',
            header: 'Last Seen',
            cell: a => (
              <span className="text-muted-foreground font-medium tabular-nums">
                <RelativeTimeWithTooltip date={a.lastSeenAt} />
              </span>
            ),
          },
        ]}
        emptyState="No Alerts"
        onRowClick={handleOnClickAlert}
        getRowId={row => row.alertKey}
      />
    </DashboardWidgetWrapper>
  );
}
